/**
 * Obstruction Wizard Navigation
 *
 * Owns the close-with-unsaved-changes flow + free section navigation. Mirrors
 * `useWizardNavigation` (tasks) but operates on the obstruction-specific form +
 * slice. Section gating is gone — sections are tabs; submit validates the whole
 * form.
 */
import { useCallback, useEffect } from 'react';
import type { UseFormReturnType } from '@mantine/form';

import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';
import type { Coordinates } from '@/types';
import type { Obstruction } from '@/types/obstructions';

import type { ObstructionWizardFormState } from '../types';
import { obstructionToFormValues } from './useObstructionForm';

interface UseObstructionWizardNavigationProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  editingObstruction: Obstruction | null;
  goToStep: (step: number) => boolean;
  anchorPoint: Coordinates | null;
}

/** Fields excluded from the shallow diff (navigation state, not user data). */
const FORM_META_FIELDS = new Set<keyof ObstructionWizardFormState>([
  'currentStep',
  'anchorPoint',
]);

/**
 * Cheap unsaved-changes heuristic.
 *
 * Create mode: dirty when ANY non-meta field is non-empty OR anchor was set.
 * Edit mode: diff current values against `obstructionToFormValues` (the
 * canonical initial-values builder) — divergence on any data field = dirty.
 * Anchor is compared separately because it lives outside the Mantine form.
 */
function detectObstructionUnsavedChanges(
  values: ObstructionWizardFormState,
  editingObstruction: Obstruction | null,
  anchorPoint: Coordinates | null
): boolean {
  if (!editingObstruction) {
    // Create mode: dirty when any data field has content or anchor was placed.
    for (const key of Object.keys(
      values
    ) as (keyof ObstructionWizardFormState)[]) {
      if (FORM_META_FIELDS.has(key)) continue;
      const value = values[key];
      if (typeof value === 'string' && value.trim().length > 0) return true;
    }
    return anchorPoint !== null;
  }

  // Edit mode: diff against the canonical initial values for this obstruction.
  const initial = obstructionToFormValues(editingObstruction);
  for (const key of Object.keys(
    values
  ) as (keyof ObstructionWizardFormState)[]) {
    if (FORM_META_FIELDS.has(key)) continue;
    if (values[key] !== initial[key]) return true;
  }

  // Anchor lives outside the Mantine form — compare separately.
  if (anchorPoint && editingObstruction.anchorPoint) {
    if (
      anchorPoint.lat !== editingObstruction.anchorPoint.lat ||
      anchorPoint.lng !== editingObstruction.anchorPoint.lng
    ) {
      return true;
    }
  }

  return false;
}

export interface UseObstructionWizardNavigationReturn {
  handleClose: () => void;
  forceClose: () => void;
  handleStepClick: (step: number) => void;
}

export function useObstructionWizardNavigation({
  form,
  editingObstruction,
  goToStep,
  anchorPoint,
}: UseObstructionWizardNavigationProps): UseObstructionWizardNavigationReturn {
  const { closeOverlay } = useArtifactNavigation();
  const setFormIsDirty = useGeoMarkupStore(
    (state) => state.obstructions.setFormIsDirty
  );
  const requestFormGuard = useGeoMarkupStore(
    (state) => state.formConflict.request
  );

  const hasUnsavedChanges = useCallback(() => {
    return detectObstructionUnsavedChanges(
      form.values,
      editingObstruction,
      anchorPoint
    );
  }, [form.values, editingObstruction, anchorPoint]);

  // Mirror dirty state into the slice so the global unsaved-changes guard can
  // decide between silent close and the confirmation prompt.
  useEffect(() => {
    setFormIsDirty(hasUnsavedChanges());
  }, [hasUnsavedChanges, setFormIsDirty]);

  // Direct close (no prompt) — used post-save and as the guard's continuation.
  const forceClose = useCallback(() => {
    form.reset();
    closeOverlay();
  }, [form, closeOverlay]);

  // User-initiated close routes through the global guard (single dialog in
  // AppShell): a dirty form prompts before discard, a clean form closes silently.
  const handleClose = useCallback(() => {
    requestFormGuard(forceClose);
  }, [requestFormGuard, forceClose]);

  return {
    handleClose,
    forceClose,
    handleStepClick: goToStep,
  };
}
